import type {AutoColor, IconName} from '@wandb/components';

/**
 * AgentSuggestion is one new-chat suggestion chip. `prompt` is the text sent on
 * click and defaults to `title`. `sentLabel` is what the sent bubble reads once
 * clicked, for chips whose title works as a button but not as something the
 * user would say; it defaults to `title`.
 */
export interface AgentSuggestion {
  title: string;
  prompt?: string;
  sentLabel?: string;
  icon: IconName;
  color: AutoColor;
}

/** Prompts launched from ARIA entry points outside the chat drawer. */
export const entryPointPrompts = {
  filterRuns: (request: string) =>
    `Update this workspace's run filters based on this request: ${request}`,
  curateRunsColumns:
    'Curate this runs table to show the most useful non-empty columns for comparing the visible runs.',
  manageRunsColumns:
    'Show and pin the most useful columns for comparing these runs.',
  createReport:
    "Create a report summarizing this project's recent results, key comparisons, and notable findings.",
  improveReport:
    'Improve this report by clarifying its conclusions and adding any important supporting analysis that is missing.',
  createSavedView:
    'Create and save a new view for this workspace. Configure its panels, filters, and columns to highlight the runs and metrics most useful for understanding this project.',
} as const;

// Suggestion chips use one consistent accent across routes.

// Shared

export const summarizeReport: AgentSuggestion = {
  title: 'Summarize this project in a report',
  icon: 'report',
  color: 'teal',
};

export const patterns: AgentSuggestion = {
  title: 'What patterns do you see in my results?',
  icon: 'chart-line',
  color: 'teal',
};

export const whatCanYouDo: AgentSuggestion = {
  title: 'What can you do?',
  prompt: '/intro',
  icon: 'help-alt',
  color: 'teal',
};

export const roastMyProject: AgentSuggestion = {
  title: 'Roast my project',
  icon: 'explosion',
  color: 'teal',
};

export const recommendNextExperiment: AgentSuggestion = {
  title: 'Recommend my next experiment',
  icon: 'lightbulb-info',
  color: 'teal',
};

// ARIA landing — onboarding CTAs. Each sends a slash command that launches the
// matching onboarding skill under the hood.

export const onboardingFineTuning: AgentSuggestion = {
  title: 'Get started with fine-tuning',
  prompt: '/onboarding-fine-tuning',
  sentLabel: 'Can you help me get started with fine-tuning?',
  icon: 'settings-parameters',
  color: 'teal',
};

export const onboardingAgentMonitoring: AgentSuggestion = {
  title: 'Get started with agent monitoring',
  prompt: '/onboarding-agent-monitoring',
  sentLabel: 'Can you help me get started with agent monitoring?',
  icon: 'automation-robot-arm',
  color: 'teal',
};

export const onboardingModelTraining: AgentSuggestion = {
  title: 'Get started with model training',
  prompt: '/onboarding-model-training',
  sentLabel: 'Can you help me get started with model training?',
  icon: 'chart-line',
  color: 'teal',
};

// Workspace

export const autoresearchQuickstart: AgentSuggestion = {
  title: 'Launch experiments with autoresearch',
  prompt: '/autoresearch-quickstart',
  icon: 'rocket-launch',
  color: 'teal',
};

export const createPanelInChat: AgentSuggestion = {
  title: 'Create a panel in this chat',
  icon: 'panel',
  color: 'teal',
};

// Default

export const recentCrashedRuns: AgentSuggestion = {
  title: 'Have any of my recent runs crashed?',
  icon: 'warning',
  color: 'teal',
};

export const starredReports: AgentSuggestion = {
  title: 'Find the reports I starred',
  icon: 'star',
  color: 'teal',
};

export const recentlyFinishedRuns: AgentSuggestion = {
  title: 'Which runs finished most recently?',
  icon: 'recent-clock',
  color: 'teal',
};

// Runs table

export const setBaseline: AgentSuggestion = {
  title: 'Set my best run as the baseline',
  icon: 'baseline-alt',
  color: 'teal',
};

// Run logs

export const analyzeLogs: AgentSuggestion = {
  title: 'Analyze my logs',
  icon: 'list-bullets',
  color: 'teal',
};

// Run (Overview)

export const compareRecentRuns: AgentSuggestion = {
  title: 'How does this run compare to recent runs?',
  icon: 'chart-scatterplot',
  color: 'teal',
};

// Non-project / org / profile

export const crashedRuns: AgentSuggestion = {
  title: 'How many of my runs crashed this past week?',
  icon: 'warning',
  color: 'teal',
};

export const activeRuns: AgentSuggestion = {
  title: 'Find all of my active runs',
  icon: 'running-repeat',
  color: 'teal',
};

export const starProjects: AgentSuggestion = {
  title: 'Star the projects I visit the most',
  icon: 'star',
  color: 'teal',
};

// Registry

export const collectionsActivity: AgentSuggestion = {
  title: 'Show me collections with recent activity',
  icon: 'recent-clock',
  color: 'teal',
};

export const usefulRegistries: AgentSuggestion = {
  title: 'Show me the most useful registries and collections for me',
  icon: 'magic-wand-star',
  color: 'teal',
};

export const cleanupRegistries: AgentSuggestion = {
  title: 'Help me clean up my registries',
  icon: 'delete',
  color: 'teal',
};

export const registriesWithAutomations: AgentSuggestion = {
  title: 'Which Registries have Automations?',
  icon: 'automation-robot-arm',
  color: 'teal',
};

// Automations

export const understandAutomations: AgentSuggestion = {
  title: 'Help me understand these automations',
  icon: 'automation-robot-arm',
  color: 'teal',
};

export const createAutomation: AgentSuggestion = {
  title: 'Help me create an automation',
  icon: 'add-new',
  color: 'teal',
};

export const describeAutomations: AgentSuggestion = {
  title: 'Describe the existing automations',
  icon: 'info',
  color: 'teal',
};

export const configureGithubAction: AgentSuggestion = {
  title: 'Help me configure a Github action',
  icon: 'github',
  color: 'teal',
};

// Weave (conversations)

export const frustratedUsers: AgentSuggestion = {
  title: 'What did frustrated users say?',
  icon: 'thumbs-down',
  color: 'teal',
};

export const summarizeConversations: AgentSuggestion = {
  title: 'Summarize the last conversation/traces',
  icon: 'forum-chat-bubble',
  color: 'teal',
};

// Weave (signals)

export const createSignal: AgentSuggestion = {
  title: 'Create a new signal',
  icon: 'add-new',
  color: 'teal',
};

export const signalsFound: AgentSuggestion = {
  title: 'What signals have you found?',
  icon: 'search',
  color: 'teal',
};

// Weave (evaluations)

export const createDataset: AgentSuggestion = {
  title: 'Help me create a dataset',
  icon: 'table',
  color: 'teal',
};

export const createScorers: AgentSuggestion = {
  title: 'Help me create scorers',
  icon: 'number-hashtag',
  color: 'teal',
};

export const evalImproved: AgentSuggestion = {
  title: 'Did my latest eval improve and why?',
  icon: 'chart-line',
  color: 'teal',
};

export const whatToTryNext: AgentSuggestion = {
  title: 'What should I try next?',
  icon: 'lightbulb-info',
  color: 'teal',
};
